// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Add / Edit Record  (the Compose flow)
//  ----------------------------------------------------------------------------
//  The "+" FAB blooms into this record. One surface, always full screen — an open
//  record in the Apple Notes spirit: ONE editor whose first line is the title, the
//  colorful rim playing through the entry and then dissolving.
//
//  THE RECORD EXISTS WHILE YOU WRITE IT. There is no save step. Opening a record
//  creates it — a card appears in the day at once, titled "New Record" — and every
//  keystroke is committed through `onLive`, so the card's title is the first
//  line as it is being typed, not a copy made later. Closing a record that was
//  left with nothing in it drops it again (md-app.jsx decides that, from the
//  same mdRecordIsBlank this sheet asks). The ✓ therefore means DONE, not save:
//  the writing was never in danger, and there is nothing to confirm.
//
//  Emergent type: a Record with no time files under "All Day"; time-binding
//  (deferred) would promote it to a timeline block. Identity is emergent too —
//  the AI dot infers a life-class domain (icon + color) from what you write, and
//  stays NEUTRAL until it's confident. Tap it to correct; that locks your choice.
//
//  Exposes <ComposeSheet> and the domain helpers md-app.jsx renders with. The
//  domain TABLE and the inference itself stay private — they are how this sheet
//  decides, not something a caller should reach past it to redo.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStateC, useEffect: useEffectC, useRef: useRefC } = React;
const { useMdDismissLayer: useEscape } = window;
const { mdRgba: rgbaC, mdVivid: vividC,
  ink: inkC, onAcc: onAccC2, MDI: MDIC, LiveMarkdown: LiveMD,
  mdRecordParts: partsC, mdRecordIsBlank: blankC, mdRecordSeedText: seedC, mdRecordUid: uidC } = window;

/* The ten life-contexts, their colours, their glyphs, and the guess that picks
   one from what you write — all of it now in md-domain.ts. It used to live here
   AND again in md-today.jsx, two copies of the same table kept in step by hand;
   projects are about to read it too. See that module's header for why the ids
   are a promise to records that already exist. */
const { MD_DOMAINS, mdDomainColor, mdDomainIcon, mdInferDomain } = window;

/* The writing column's maximum width. Only bites on the canvas, where the record
   is as wide as the window allows; in the 420pt panel it is never reached. */
const MD_MEASURE = 780;

/* The sheet's corner and its two planes are md-sheet-size.ts's now — the same
   module the ladder and the rectangle come from, so there is one answer to
   "how big, how round, how high" rather than three constants in three files.
   `--md-radius-sheet` is the corner; these are the storeys. */
const { MD_SHEET_Z, MD_SHEET_SCRIM_Z, MD_INFO_PANEL_W } = window;

/* The size control's glyphs and its menu, taken from md-database.jsx the same
   way md-projects.jsx takes them (line 85) — one vocabulary for "make this
   bigger", not a second drawn here. Destructured at load, which index.html
   already guarantees: that file is the line above this one. */
/* DbPanel and DbCommand came out with the "Open in" menu (Marwan,
   2026-08-21). DbGlyph stays: the two steps still wear DbRowPage's glyphs. */
const { DbGlyph } = window;

/* And the silence with them, for the same reason: the sheet is one more layer
   on the windows' stack, not a second opinion about who is on top. The fallback
   keeps the hook count steady for any harness that mounts this file alone. */
const useDbInertBackgroundC = window.useDbInertBackground
  || ((refs, active) => { useEffectC(() => undefined, [active]); });

/* B5 — CHANGING RUNG ANIMATES THE FRAME, NEVER THE TEXT. Every property
   mdSheetFrame can move, and nothing else: the writing column is capped at
   MD_MEASURE at every rung, so a line cannot re-wrap on the way — what travels
   is the room around it. Named once because it is spliced into two different
   transition lists, and a fourth property added to the frame and forgotten here
   would snap while its three neighbours glide. `prefers-reduced-motion` is
   answered app-wide in index.html, which cuts every transition in the app to
   nothing; a second answer here would be a second thing to keep in step. */
const MD_FRAME_EASE = '.42s cubic-bezier(.32,.72,.28,1)';
const MD_FRAME_MOTION = ['inset-block-start', 'inset-block-end',
  'inset-inline-start', 'inset-inline-end', 'inline-size', 'block-size',
  'border-radius', 'padding'].map((p) => `${p} ${MD_FRAME_EASE}`).join(', ');

/* The lead tile before a project's title line: its size, the room the title
   reserves for it, and the offset that puts its BOTTOM EDGE on the title's
   baseline rather than merely near it.

   MD_LEAD_TOP is measured, not guessed — against .md-ln-title at 21px /
   line-height 1.25 inside the editor's 8px of top padding (see index.html).
   The check is a zero-height inline-block probe appended to the title line,
   whose own bottom sits exactly on the baseline; tile bottom minus probe
   bottom must be 0. Change the title's type and this has to be re-measured. */
const MD_LEAD_SIZE = 26;
const MD_LEAD_GAP = 9;
const MD_LEAD_TOP = 2.5;

/* HH:MM string + a compact clock-window label for a time-bound (grid) Record */
const mdSnap5 = (m) => Math.max(0, Math.min(1435, Math.round(m / 5) * 5));

/* A stored target is a day key; WhenPicker speaks offsets from today. Nothing
   durable is ever an offset — see md-theme.jsx on why everything is filed under
   a day key — so the conversion happens at the picker's edge, both ways. */
function mdOffsetForKey(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof key === 'string' ? key : '');
  return m && window.mdOffsetForDate ? window.mdOffsetForDate(+m[1], +m[2] - 1, +m[3]) : null;
}
function mdMinToHHMMc(m) {const mm = (m % 1440 + 1440) % 1440;return `${String(Math.floor(mm / 60)).padStart(2, '0')}:${String(mm % 60).padStart(2, '0')}`;}
function winLabelC(min, dur) {
  const part = (m) => {const mm = (m % 1440 + 1440) % 1440,H = Math.floor(mm / 60),M = mm % 60;return { h: H % 12 || 12, m: String(M).padStart(2, '0'), ap: H >= 12 ? 'PM' : 'AM' };};
  const a = part(min),b = part(min + dur);
  return `${a.h}:${a.m}${a.ap !== b.ap ? ' ' + a.ap : ''}–${b.h}:${b.m} ${b.ap}`;
}


/* ── toolbar icons — quiet single-weight outline glyphs (color is earned) ── */
const TIcon = {
  calendar: (c, w = 20) => MDIC.calendar(c, w),
  tag: (c, w = 20) => <svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M4 4.5h7.2L20 13.3a2 2 0 010 2.8l-4 4a2 2 0 01-2.8 0L4.5 11.7V4.5z" stroke={c} strokeWidth="1.7" strokeLinejoin="round" /><circle cx="8.4" cy="8.4" r="1.4" fill={c} /></svg>,
  attach: (c, w = 20) => <svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M19 11.5l-7.1 7.1a4.4 4.4 0 01-6.2-6.2l7.4-7.4a2.9 2.9 0 014.1 4.1l-7.3 7.3a1.4 1.4 0 01-2-2l6.6-6.6" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  link: (c, w = 20) => <svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M10.3 13.7a4 4 0 005.7.1l2-2a4 4 0 00-5.7-5.7l-1.2 1.2M13.7 10.3a4 4 0 00-5.7-.1l-2 2a4 4 0 005.7 5.7l1.2-1.2" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  project: (c, w = 20) => <svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><rect x="3.5" y="4.5" width="7" height="7" rx="1.8" stroke={c} strokeWidth="1.7" /><rect x="13.5" y="4.5" width="7" height="7" rx="1.8" stroke={c} strokeWidth="1.7" /><rect x="3.5" y="14.5" width="7" height="5.5" rx="1.8" stroke={c} strokeWidth="1.7" /><rect x="13.5" y="14.5" width="7" height="5.5" rx="1.8" stroke={c} strokeWidth="1.7" /></svg>,
  ai: (c, w = 20) => <svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M12 3.5l1.7 4.3 4.3 1.7-4.3 1.7L12 15.5l-1.7-4.3L6 9.5l4.3-1.7L12 3.5z" stroke={c} strokeWidth="1.6" strokeLinejoin="round" /><path d="M18.5 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" stroke={c} strokeWidth="1.4" strokeLinejoin="round" /></svg>,
  // minus — "minimize a draft": the sheet is put away, the draft is kept (not discarded)
  minus: (c, w = 22) => MDIC.minus(c, w),
  // more — overflow “others” menu (vertical ellipsis)
  more: (c, w = 20) => MDIC.more(c, w)
};

/* ── keyboard priming ────────────────────────────────────────────────────────
   iOS raises the software keyboard only when focus() runs SYNCHRONOUSLY inside
   the user's tap — the editor's own autofocus fires after mount, which iOS
   ignores. So the tap that opens compose first focuses this hidden input, and
   the editor takes the focus over when it mounts (focus moving between two
   editables keeps the keyboard up). No-op where there is no touch keyboard. */
let mdKbProxy = null;
function mdPrimeKeyboard() {
  if (!('ontouchstart' in window)) return; // desktop: never needed
  if (!mdKbProxy) {
    mdKbProxy = document.createElement('input');
    mdKbProxy.type = 'text';
    mdKbProxy.setAttribute('aria-hidden', 'true');
    mdKbProxy.tabIndex = -1;
    Object.assign(mdKbProxy.style, { position: 'fixed', top: '0', left: '0', width: '1px', height: '1px',
      opacity: '0', border: '0', padding: '0', fontSize: '16px' }); // 16px stops the iOS focus-zoom
    document.body.appendChild(mdKbProxy);
  }
  try {mdKbProxy.focus({ preventScroll: true });} catch (e) {mdKbProxy.focus();}
}

/* ════════════════════════════════════════════════════════════════════════════
   THE RECORD'S OWN CONTROLS (P-3 / P-4)
   ---------------------------------------------------------------------------
   Marwan, 2026-08-21: "the New Project Sheet Looks and acts Perfect … I want to
   clone it with all its details and styles of buttons and interactions and
   everything and make it the sole layout template for compose sheets."

   So most of the record's properties are not drawn here at all. They are the
   beginning's own pills — `NpChoice`, `NpTags`, `NpDomain`, and the `npBox`
   dress and `NpMenu` panel they hang from — mounted through `window.MdNp`,
   which md-projects.jsx publishes and this file only reads. Publishing rather
   than moving is the whole point: the beginning is frozen (P-5's guard in
   tests/beginning-surface.test.js), and a component that has been moved is a
   component that has been edited.

   What lives HERE is only the part a project has no equivalent of: which
   project a record belongs to, and the window of time it occupies. Both wear
   `npBox`, so they are the same object to look at and to press as the pills
   either side of them.

   ── WHAT THIS REPLACED ────────────────────────────────────────────────────
   `AiDot` and `ComposeToolbar`, which stood at the foot of the record. The
   toolbar's own comment recorded that a Projects control had lived in that bar
   once and was pulled out because four 44px tools plus the elastic When label
   overran it by 11px — so the project and the part of life were folded into
   one coloured circle. That was an arrangement forced by arithmetic rather
   than chosen. Nothing is short of room now: the project gets a word, the part
   of life gets the tile the beginning draws beside a name, and the window of
   time gets a pill that says what it is.
   ═════════════════════════════════════════════════════════════════════════ */

/* ── WHICH PROJECT THIS RECORD BELONGS TO ────────────────────────────────────
   The beginning's crumb reads `[Pursuit ▾] › New project`, because a project is
   born into a pursuit or into nothing. A record is born into a PROJECT or into
   nothing, so the crumb says that instead — Marwan's 2.2, in his own words:
   "the Pursuit pill should be Projects in the new record sheet, and the New
   Project pill should say New Record."

   `New project…` opens INLINE, for the reason `AiDot`'s inline creator existed:
   leaving a sheet to make the thing the sheet just asked you for loses whatever
   you had typed. The part of life is not asked for — by the time this is
   reachable the record already has one, picked or inferred from the writing. */
function RecordProject({ accent, pid, domain, onPick, onCreate }) {
  const light = window.mdIsLight();
  const Np = window.MdNp || {};
  const [open, setOpen] = useStateC(null);
  const [naming, setNaming] = useStateC(false);
  const [draft, setDraft] = useStateC('');
  const btn = useRefC(null);
  if (!Np.NpMenu) return null;
  const project = pid && window.mdProjectById ? window.mdProjectById(pid) : null;
  const rows = window.mdProjectIndex ? window.mdProjectIndex({ open: true }) : [];
  const close = () => { setOpen(null); setNaming(false); setDraft(''); };
  const make = () => {
    const clean = draft.trim();
    if (!clean) return;
    const made = onCreate(clean, domain);
    if (made) close();
  };
  return <span className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" ref={btn} aria-haspopup="menu"
      aria-expanded={open ? 'true' : 'false'}
      aria-label={project ? `Project — ${project.name}` : 'Project — none'}
      onClick={(e) => (open ? close() : setOpen(Np.npPlace(e.currentTarget)))}
      style={{ ...Np.npBox(light, !!project), maxWidth: 240, overflow: 'hidden' }}>
      <span dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' }}>{project ? project.name : 'No project'}</span>
    </button>
    {open && <Np.NpMenu title="Project" at={open} accent={accent} anchor={btn} onClose={close}>
      {naming
        ? <div style={{ padding: '2px 8px 8px', display: 'flex', flexDirection: 'column',
          gap: 8, width: 216 }}>
          <input aria-label="New project name" autoFocus dir="auto" value={draft}
            placeholder="What are you working on?"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') make(); }}
            style={{ width: '100%', boxSizing: 'border-box', minHeight: 30, padding: '6px 8px',
              border: `1px solid ${inkC(light ? 0.1 : 0.12)}`,
              borderRadius: 'var(--md-radius-control, 8px)', background: 'transparent',
              color: inkC(1), font: 'inherit', fontSize: 13, outline: 'none' }} />
          <button type="button" aria-label="Create project" disabled={!draft.trim()}
            onClick={make}
            style={{ minHeight: 28, border: 'none', borderRadius: 'var(--md-radius-control, 8px)',
              background: draft.trim() ? rgbaC(accent, 0.95) : inkC(0.07),
              color: draft.trim() ? onAccC2() : inkC(0.3), font: 'inherit',
              fontSize: 12.5, fontWeight: 640,
              cursor: draft.trim() ? 'pointer' : 'default' }}>Create project</button>
        </div>
        : <div role="listbox" aria-label="Project">
          {rows.map((row) => {
            const on = pid === row.id;
            return <button key={row.id} type="button" role="option"
              aria-selected={on ? 'true' : 'false'} className="md-db-menu-row"
              onClick={() => { onPick(row.id); close(); }}>
              <span dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' }}>{row.name}</span>
              {on ? MDIC.check(inkC(0.55), 13) : null}
            </button>;
          })}
          {/* Leaving one is an answer too, and it is a row rather than a
              corner link because that is how the beginning's pills clear. */}
          {pid ? <button type="button" className="md-db-menu-row"
            onClick={() => { onPick(null); close(); }}>
            <span>No project</span>
          </button> : null}
          <button type="button" className="md-db-menu-row"
            onClick={() => setNaming(true)}>
            <span>New project…</span>
          </button>
        </div>}
    </Np.NpMenu>}
  </span>;
}

/* ── THE WINDOW OF TIME, IN ONE PILL (P-4) ───────────────────────────────────
   Marwan, 2026-08-21: "for new records The start and target should be merged
   into one pill, which sets the time and duration as applied in the scheduler
   logic, and that pill should open the scheduler popup."

   This is the ONE place the two sheets differ, and the difference is not a
   style choice — it is what the two things are. A project AIMS at a day: it
   starts around here and wants to be done by then, two dates, each a day,
   which is why the beginning wears two `NpDay` pills over a month grid. A
   record OCCUPIES a window: this day, from this minute, for this long. Three
   facts that are one answer, so they are one pill — and a month grid cannot
   say 1:15 PM, so it opens the scheduler the whole app already schedules with
   (`ScheduleOverlay`), which returns exactly `{offset, min, dur}`.

   Nothing about it is drawn here except its word: the dress is `npBox`. */
function RecordWhen({ accent, label, dayLabel, schedText, onOpen }) {
  const light = window.mdIsLight();
  const Np = window.MdNp || {};
  if (!Np.npBox) return null;
  const said = [dayLabel, schedText].filter(Boolean).join(' · ');
  return <span data-md-np-pill="when" className={light ? 'md-db-light' : ''}
    style={{ display: 'inline-flex', '--db-accent': accent }}>
    <button type="button" aria-label={`${label} — ${said || 'none'}`}
      aria-haspopup="dialog" onClick={onOpen}
      style={{ ...Np.npBox(light, !!said), maxWidth: 280, overflow: 'hidden' }}>
      <span style={{ display: 'inline-flex', lineHeight: 0, flexShrink: 0 }}>
        {TIcon.calendar(inkC(said ? 0.6 : 0.42), 14)}</span>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        fontVariantNumeric: 'tabular-nums' }}>{said || label}</span>
    </button>
  </span>;
}

/* ── WHERE THIS RECORD STANDS ────────────────────────────────────────────────
   `NpChoice` again — the beginning's own status pill, the same silhouettes and
   the same menu — over a RECORD's vocabulary instead of a project's.

   The two differ only in where the answer is written, and that difference is
   deliberately not this component's business: `onPick` is handed down, and the
   sheet decides whether the word goes to md-app's door, to md-project.ts's, or
   into a seed for a row the store has not heard of yet (CS-10). One control,
   three destinations, and the pill never learns which. */
function RecordStatus({ accent, record, scheduled, pending, onPick }) {
  const Np = window.MdNp || {};
  const MdWs = window.MdWs || {};
  if (!Np.NpChoice) return null;
  const flow = window.mdWorkflowForRecord ? window.mdWorkflowForRecord(record) : null;
  const statuses = (flow && flow.statuses) || [];
  const stored = window.mdRecordStatusOf ? window.mdRecordStatusOf(record, scheduled) : null;
  const seeded = pending !== null && pending !== undefined
    ? statuses.find((row) => row.id === pending) || null : null;
  const here = seeded || stored;
  const rowOf = (id) => statuses.find((s) => s.id === id) || null;
  return <Np.NpChoice field="status" label="Status" value={here ? here.id : null}
    accent={accent} options={statuses.map((row) => row.id)}
    labelOf={(id) => { const row = rowOf(id); return row ? row.name : 'Inbox'; }}
    mark={MdWs.StatusMark
      ? (value) => <MdWs.StatusMark category={rowOf(value) ? rowOf(value).category : null}
        color={rgbaC(accent, 1)} />
      : null}
    clearable={false} onPick={onPick} />;
}

/* The tag inspector — one component, two placements (CS-11).
   `framed` is the popover it used to always be: a floating dialog with a back
   button to the More menu. Unframed it is the Info Panel's Tags section, and
   the body is byte-identical either way, which is the whole point. The panel
   absorbed this rather than growing its own tag list, because two lists of the
   same tags would drift the first time one of them was fixed. */
function TagInspector({ light, accent, active, options, sourceRef, onBack, onAdd, onRemove, onOpen,
  framed = true }) {
  const [query, setQuery] = useStateC('');
  const [selected, setSelected] = useStateC(() => active[0] ? active[0].key : '');
  const sourceKey = window.mdWikiTargetKey(sourceRef);
  const q = query.trim().toLocaleLowerCase();
  const rows = (options || []).filter((row) => !q || row.key.includes(q) || row.name.toLocaleLowerCase().includes(q));
  const exact = q && rows.find((row) => row.key === (window.mdTagKey ? window.mdTagKey(query) : q));
  const createName = !exact && window.mdTagName ? window.mdTagName(query) : null;
  const chosen = (options || []).find((row) => row.key === selected);
  const records = chosen ? (chosen.records || []).filter((row) => window.mdWikiTargetKey(row.ref) !== sourceKey) : [];
  const isActive = (key) => active.some((tag) => tag.key === key);
  const frame = framed ? {
    position: window.innerWidth < 500 ? 'fixed' : 'absolute',
    bottom: window.innerWidth < 500 ? 92 : 52,
    insetInlineStart: window.innerWidth < 500 ? 10 : 'auto',
    insetInlineEnd: window.innerWidth < 500 ? 'auto' : 0,
    zIndex: 41, width: 'min(312px, calc(100vw - 20px))', maxHeight: 440, overflowY: 'auto', padding: 7,
    borderRadius: 16, animation: 'mdSlotIn .16s ease',
    background: light ? 'rgba(252,253,255,0.97)' : 'rgba(30,32,40,0.97)',
    border: `0.75px solid ${inkC(light ? 0.08 : 0.16)}`,
    boxShadow: `0 14px 34px ${light ? 'rgba(30,42,72,0.22)' : 'rgba(0,0,0,0.55)'}`,
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  } : { margin: '0 -8px' };
  return <div {...(framed ? { role: 'dialog', 'aria-label': 'Tags' } : {})}
    className={framed ? 'md-scroll' : ''} style={frame}>
    {framed &&
    <div style={{ display: 'flex', alignItems: 'center', minHeight: 36, padding: '0 5px 4px' }}>
      <button aria-label="Back to More" onClick={onBack}
        style={{ width: 32, height: 32, display: 'grid', placeItems: 'center', border: 'none',
          background: 'none', color: inkC(0.62), cursor: 'pointer', fontSize: 21 }}>‹</button>
      <span style={{ flex: 1, fontSize: 14, fontWeight: 680, color: inkC(0.9) }}>Tags</span>
      {!!active.length && <span style={{ fontSize: 11.5, fontWeight: 650,
        color: inkC(0.4), fontVariantNumeric: 'tabular-nums' }}>{active.length}</span>}
    </div>}

    {!!active.length && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 7px 10px' }}>
      {active.map((tag) => <span key={tag.key} style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0,
        height: 30, borderRadius: 9, background: selected === tag.key ? rgbaC(accent, light ? 0.14 : 0.2) : inkC(light ? 0.055 : 0.09),
        boxShadow: selected === tag.key ? `inset 0 0 0 0.75px ${rgbaC(accent, 0.34)}` : 'none' }}>
        <button onClick={() => setSelected(tag.key)} style={{ height: '100%', maxWidth: 184, padding: '0 3px 0 9px',
          border: 'none', background: 'none', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', color: selected === tag.key ? rgbaC(accent, 1) : inkC(0.72), fontSize: 12.5, fontWeight: 650 }}>
          #{tag.name}
        </button>
        <button aria-label={`Remove tag ${tag.name}`} onClick={() => {onRemove(tag.name);if (selected === tag.key) setSelected('');}}
          style={{ width: 28, height: 28, border: 'none', background: 'none', cursor: 'pointer',
            display: 'grid', placeItems: 'center', padding: 0 }}>{MDIC.close(inkC(0.42), 13)}</button>
      </span>)}
    </div>}

    <div style={{ margin: '0 6px 6px', height: 38, display: 'flex', alignItems: 'center', gap: 8,
      padding: '0 10px', borderRadius: 10, background: inkC(light ? 0.045 : 0.08),
      boxShadow: `inset 0 0 0 0.75px ${inkC(light ? 0.08 : 0.13)}` }}>
      <span aria-hidden="true" style={{ color: inkC(0.42), fontSize: 15, fontWeight: 700 }}>#</span>
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find or create tag"
        aria-label="Find or create tag" style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none',
          background: 'transparent', color: inkC(0.86), fontSize: 13.5, fontFamily: 'inherit' }} />
    </div>

    {(createName || rows.length > 0) && <div style={{ padding: '2px 0 5px' }}>
      {createName && <button onClick={() => {onAdd(createName);setSelected(window.mdTagKey(createName));setQuery('');}}
        style={{ width: '100%', minHeight: 40, display: 'flex', alignItems: 'center', gap: 9, padding: '5px 10px',
          border: 'none', borderRadius: 10, background: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ width: 24, height: 24, borderRadius: 8, display: 'grid', placeItems: 'center',
          background: rgbaC(accent, light ? 0.12 : 0.18), color: rgbaC(accent, 1), fontSize: 17 }}>+</span>
        <span style={{ fontSize: 13.5, fontWeight: 620, color: inkC(0.84) }}>Create #{createName}</span>
      </button>}
      {rows.slice(0, 12).map((row) => {
        const on = isActive(row.key);
        return <button key={row.key} onClick={() => {if (!on) onAdd(row.name);setSelected(row.key);setQuery('');}}
          style={{ width: '100%', minHeight: 40, display: 'flex', alignItems: 'center', gap: 9, padding: '5px 10px',
            border: 'none', borderRadius: 10, background: selected === row.key ? rgbaC(accent, light ? 0.09 : 0.14) : 'none',
            cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ width: 24, textAlign: 'center', color: on ? rgbaC(accent, 1) : inkC(0.42),
            fontSize: on ? 14 : 13, fontWeight: 700 }}>{on ? '✓' : '#'}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontSize: 13.5, fontWeight: 620, color: inkC(0.82) }}>#{row.name}</span>
          <span style={{ fontSize: 11.5, color: inkC(0.4), fontVariantNumeric: 'tabular-nums' }}>{row.count}</span>
        </button>;
      })}
    </div>}

    {/* `onOpen` is the gate on the whole section, not just on the row's click.
        A project draft has nowhere to go — following a record from it would
        throw away writing that exists nowhere else — so it hands over none, and
        the list is not drawn rather than drawn dead. */}
    {chosen && onOpen && <div style={{ borderTop: `0.75px solid ${inkC(light ? 0.08 : 0.13)}`, padding: '7px 0 2px' }}>
      <div style={{ padding: '3px 10px 5px', fontSize: 10.5, fontWeight: 700,
        letterSpacing: 0.7, textTransform: 'uppercase', color: inkC(0.42) }}>Records</div>
      {!records.length && <div style={{ padding: '8px 10px 11px', fontSize: 12.5, color: inkC(0.42) }}>
        No other records use this tag.
      </div>}
      {records.map((row, i) => <button key={`${row.ref.type}:${row.ref.id}:${i}`} onClick={() => onOpen(row.ref)}
        style={{ width: '100%', minHeight: 42, display: 'flex', alignItems: 'center', gap: 9,
          padding: '6px 10px', border: 'none', borderRadius: 10, background: 'none', textAlign: 'left', cursor: 'pointer' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: rgbaC(accent, 0.7), flexShrink: 0 }} />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            fontSize: 13.5, fontWeight: 620, color: inkC(0.84) }}>{row.title}</span>
          {row.subtitle && <span style={{ display: 'block', marginTop: 1, fontSize: 11, color: inkC(0.4) }}>{row.subtitle}</span>}
        </span>
      </button>)}
    </div>}
  </div>;
}


/* ── the sheet head — the "In The Name of Allah" blessing on one line with the
   minus (minimize). The category dot used to hold the left corner and now lives
   in the toolbar, so the blessing needs a counterweight to stay centred on the
   RECORD rather than centred in the room left over beside the minus. ── */
/* ── THE SIZE CONTROL (CS-6) ──────────────────────────────────────────────────
   DbRowPage solved this shape already: a glyph that steps the size. Same
   glyphs, because a second grammar for the same act is one more thing to learn
   for nothing.

   NOTHING IS OFFERED THAT CANNOT SUCCEED. A screen whose ladder has one rung
   gets no control at all, not a disabled one; there is no expand at the top and
   no shrink at the bottom. A button that does nothing teaches you not to trust
   buttons.

   ── AND THE "OPEN IN" MENU BESIDE THEM IS GONE (Marwan, 2026-08-21) ────────
   Borrowing it from DbRowPage was the right instinct and the wrong arithmetic.
   DbRowPage names FOUR destinations — a side peek, a centre peek, the full
   page, a new tab — and no glyph can step between four things, so there a menu
   is the only way to reach them. This ladder has TWO rungs, and the button
   beside the menu already moved between them in one press. What the menu
   offered, then, was a list containing the rung you are standing on and the
   one the neighbouring button goes to: a second control for an act that was
   never more than one press away, and a third button in a head that had just
   spent a whole pass getting down to two.

   The rung names went with it. They existed only so the menu could print them,
   and a lookup table nothing reads from is one that goes stale in silence. */
function SheetSizer({ size, sizes, onChoose, ghostBtn, press }) {
  const at = sizes.indexOf(size);
  const canGrow = at >= 0 && at < sizes.length - 1;
  const canShrink = at > 0;
  if (sizes.length < 2) return null;
  const step = (dir) => onChoose(sizes[at + dir]);
  return (
    <React.Fragment>
      {canGrow &&
      <button aria-label="Expand" title="Expand  ⌘⌥↑" onClick={() => step(1)} {...press}
        style={{ ...ghostBtn, color: inkC(0.34) }}>
        <DbGlyph name="expand" size={17} color={inkC(0.34)} />
      </button>}
      {canShrink &&
      <button aria-label="Shrink" title="Shrink  ⌘⌥↓" onClick={() => step(-1)} {...press}
        style={{ ...ghostBtn, color: inkC(0.34) }}>
        <DbGlyph name="shrink" size={17} color={inkC(0.34)} />
      </button>}
    </React.Fragment>);

}

/* ── THE ASSENT, IN THE SHEET'S OWN CORNER (Marwan, 2026-08-21) ──────────────
   Both sheets end with one round accent button, and both of them used to sit at
   the end of the WRITING'S measure rather than at the sheet's edge — 40pt in
   from the side on a popup and 16 up from the bottom, so a circle that reads as
   nested in the sheet's rounded corner was in fact floating short of it, by a
   different amount in each direction. The measure is the law for words; it was
   never the law for a control that belongs to the surface.

   So it is placed against the sheet, one inset on both axes, and the two sheets
   share the number. It is still INSIDE the writing column rather than over the
   whole frame: when the Details panel docks, the corner it means is the
   record's corner, not the panel's.

   `ready` because the two sheets' laws differ and the button has to say so
   (D5): a record was saved from the first keystroke, so its ✓ is never refused;
   a project writes nothing until this is pressed, so with no name there is
   nothing to press it for, and a control that cannot succeed is not offered
   as though it could (rule 1). */
const MD_SHEET_INSET = 18;

function MdSheetDone({ accent, label, ready = true, onPress, attrs = null }) {
  const live = ready !== false;
  return <button type="button" aria-label={label} disabled={!live} onClick={onPress}
    {...(attrs || {})}
    style={{ position: 'absolute', insetInlineEnd: MD_SHEET_INSET, bottom: MD_SHEET_INSET,
      width: 46, height: 46, borderRadius: '50%', border: 'none', flexShrink: 0, zIndex: 8,
      display: 'grid', placeItems: 'center',
      background: live ? accent : inkC(0.07),
      boxShadow: live
        ? `0 6px 16px ${rgbaC(accent, 0.45)}, inset 0 1px 1px rgba(255,255,255,0.5)`
        : 'none',
      cursor: live ? 'pointer' : 'default',
      transition: 'background .2s ease, box-shadow .2s ease, transform .12s ease' }}
    onPointerDown={(e) => {if (live) e.currentTarget.style.transform = 'scale(0.9)';}}
    onPointerUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
    onPointerLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
    {MDIC.check(live ? onAccC2() : inkC(0.3), 24)}
  </button>;
}

/* ── THE FOUR CORNERS (Marwan, 2026-08-21) ───────────────────────────────────
   The head used to hold every control in one trailing cluster: size, then
   "Open in", then the way out. Three buttons in a row, and the destructive one
   — the × that ends a beginning — sat one pixel from the one you press to get
   room to write. The old comment beside them argued the order was safe because
   the harmless control came first, which is an argument that the DISTANCE is
   the safety. There is a whole corner of distance available, so it is spent.

   The way out takes the LEADING corner. Size and Details take the trailing one,
   with assent in the trailing corner below (md-projects.jsx, and the toolbar's
   ✓). Four corners, four jobs, and the two that cannot be undone — leaving, and
   committing — are diagonally opposite: the farthest apart two controls on one
   surface can be.

   `closeLabel` because the two sheets that stand in this head leave by
   different laws (D5) and the button has to say which. A record is already
   saved, so its word is "Minimize" — put away, not finished with. A project
   draft is not saved, so its way out ENDS it, and the word says out loud that
   nothing was begun.

   D5 WAS SETTLED IN THE WORDS, NOT THE MARK (Marwan, 2026-08-24, polish 6
   follow-up). It used to be minus versus ×, then bare minus versus ringed —
   and each split read as one screen missing the app's mark. Every dismissal
   in the app is now the one ringed minus at the one size; the LABEL carries
   the opposite promises. */
function SheetHead({ light, accent, onMinimize, size, sizes, onSize,
  closeLabel = 'Minimize', onDetails = null, detailsOn = false }) {
  const ghostBtn = {
    width: 36, height: 36, borderRadius: 11, border: 'none', cursor: 'pointer', flexShrink: 0,
    display: 'grid', placeItems: 'center', background: 'none',
    transition: 'transform .12s ease, opacity .15s ease', opacity: 0.9 };
  const press = {
    onPointerDown: (e) => {e.currentTarget.style.transform = 'scale(0.85)';e.currentTarget.style.opacity = '0.6';},
    onPointerUp: (e) => {e.currentTarget.style.transform = 'scale(1)';e.currentTarget.style.opacity = '0.9';},
    onPointerLeave: (e) => {e.currentTarget.style.transform = 'scale(1)';e.currentTarget.style.opacity = '0.9';} };
  return (
    /* One line. The blessing is CENTRED ON THE SHEET, not balanced against the
       controls: it used to be a 28px spacer counterweighting one minus button,
       and a counterweight stops being one the moment the other side can hold
       one, two or three controls depending on what the ladder offers here.
       Laid over the row rather than in it, so it is on the record's true centre
       at every rung and cannot be pushed off it by a button appearing.

       18px of top padding at every size: `full` clears the status bar with the
       device's own safe-area inset, spent once on the sheet's border box
       (md-sheet-size.ts), so this row no longer needs a number for it. */
    <div style={{ flexShrink: 0, position: 'relative', zIndex: 8, display: 'flex', alignItems: 'center', gap: 8,
      padding: '18px 18px 4px' }} data-comment-anchor="12477ba2e6-div-260-5">
      {/* the blessing — said before beginning anything, and now said in one
          place: beginning a PROJECT opens with the same line, and two copies of
          this sentence is not a thing to have (md-theme.jsx). */}
      <span aria-hidden="false" style={{ position: 'absolute', insetInline: 18, top: 18,
        height: 36, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
        {window.MdBlessing ? <window.MdBlessing /> : null}
      </span>
      {/* the way out, alone in the leading corner — the app's one ringed
          dismissal, for a saved record and an unwritten draft alike (Marwan's
          call, Polish #6: the bare-minus distinction read as the mark simply
          missing). The label still tells a put-away from a discard. Nothing
          else is within reach of it. */}
      <window.MdDismiss label={closeLabel} onClick={onMinimize}
        style={{ marginInlineStart: -8 }} {...press} />
      {/* how big, and what this is — the two questions you ask ABOUT the sheet,
          in the corner opposite the one that ends it. */}
      <span style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        <SheetSizer size={size} sizes={sizes} onChoose={onSize}
          ghostBtn={ghostBtn} press={press} />
        {/* Details — the same panel the properties band opens, reachable from
            the head so it does not depend on a band this sheet may not draw.
            A TOGGLE, aria-expanded and all: the thing that opened it is the
            obvious thing to press to put it away, and a panel you can only
            close from inside is a door with a handle on one side. */}
        {onDetails &&
        <button aria-label="Details" title="Details" aria-expanded={detailsOn}
          onClick={onDetails} {...press}
          style={{ ...ghostBtn, marginInlineEnd: -8, opacity: 1,
            background: detailsOn ? inkC(light ? 0.07 : 0.12) : 'none' }}>
          <PanelGlyph color={detailsOn ? rgbaC(accent, 1) : inkC(0.34)} />
        </button>}
      </span>
    </div>);

}

/* A sheet with a column held off its trailing edge — the shape the docked panel
   makes, drawn at 17px. It is the layout, not a metaphor for it: pressed on a
   wide sheet this is literally what happens next. Which is exactly why it is
   MIRRORED in Arabic (B6): an icon that claims to be the layout and then points
   at the wrong edge is worse than one that never claimed it. */
function PanelGlyph({ color }) {
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"
    style={rtl ? { transform: 'scaleX(-1)' } : null}>
    <rect x="3" y="4.5" width="18" height="15" rx="3" stroke={color} strokeWidth="1.7" />
    <path d="M14.5 4.5v15" stroke={color} strokeWidth="1.7" />
  </svg>;
}

/* ════════════════════════════════════════════════════════════════════════════
   THE STAND, AND THE FRAME IT DRAWS (CS-8)
   ---------------------------------------------------------------------------
   Beginning a project and beginning a record are the same act — you type a name
   into a surface that rose over what you were looking at — and until now they
   were two surfaces that only LOOKED alike. The project's had its own portal,
   its own scrim, its own two-state expand button, its own dimensions and its own
   animation, none of which knew about any of this. They had already drifted: the
   project sheet floated at 620 where the popup is 880, it had no ladder, and the
   size you chose on it was forgotten the moment it closed.

   So this is the frame, extracted whole, and the two sheets are its two
   tenants. `useMdSheetStand` is where a sheet STANDS — which rungs this screen
   offers, which one it is on, the keyboard that steps them, and the rise-in and
   fall-out. `MdSheetFrame` is what that draws: the portal, the scrim, the
   rectangle, the rim, the head.

   ── NO MEMORY, BECAUSE IT IS A SHEET (Marwan, 2026-08-21) ───────────────
   CS-4 gave both sheets the memory the three windows have, on the reasoning
   that how big you like a sheet is a fact about sheets. Driven, that turned out
   to be a fact about WINDOWS. A window is a place you go back to and arrange; a
   sheet is a thing you open to write one line and close. Spreading one out to
   full screen once meant every record after it opened by taking the whole
   screen — and there is no moment where a person decides they are done with
   that, because they are not thinking about sheet sizes at all, they are just
   closing sheets.

   So the rung lives for as long as the sheet does. A popup is the answer to
   "let me write something down"; anything bigger is a choice about THIS piece
   of writing, and it ends with it. The three windows keep their memory, which
   is the difference stated: `sheet` is no longer one of md-ui's surfaces.

   ── WHAT IT DELIBERATELY DOES NOT SHARE ─────────────────────────────────
   The write law (D5). A record exists from the first keystroke; a project writes
   nothing until Create. The frame does not know which of those it is holding —
   it takes `onClose` and asks no questions — because a frame that knew would be
   a frame with two behaviours, which is the thing this extraction exists to stop
   there being.
   ═════════════════════════════════════════════════════════════════════════ */
function useMdSheetStand(onExit) {
  const L = window.useMdLayout ? window.useMdLayout() : null;
  const ladder = window.mdSheetSizes(L);
  /* Every sheet opens on its shape's default. Resolved through the ladder on
     every render rather than only at mount, so a window dragged narrow — or an
     iPad rotated — under an open sheet still draws a rung this shape has. */
  const [chosen, setSize] = useStateC(ladder.initial);
  const size = window.mdSheetSizeResolve(chosen, L);
  const [enter, setEnter] = useStateC(false);   // play the rise-in
  const [leaving, setLeaving] = useStateC(false); // play the fall-out
  // ceremony, then calm: the conic rim traces the frame's edge during the
  // rise-in, then dissolves and unmounts — what is on the sheet belongs to the
  // writing after that.
  const [rim, setRim] = useStateC('on'); // 'on' → 'fading' → 'off'

  /* B7 — THE KEYBOARD REACHES THE LADDER. Somebody who lives on the keyboard
     should never have to find a button to get room to write. Vertical arrows on
     purpose: left and right swap meaning in Arabic, and a shortcut that grows
     the sheet in English and shrinks it in Arabic is not a shortcut. Both
     modifiers, so nothing here is ever mistaken for the caret's own arrows.
     `mdSheetStep` stops at the ends rather than wrapping, and setSize refuses a
     rung this screen does not offer, so the two ends need no test here. */
  useEffectC(() => {
    if (ladder.sizes.length < 2) return undefined;
    const onKey = (e) => {
      if (!e.altKey || !(e.metaKey || e.ctrlKey)) return;
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      setSize(window.mdSheetStep(size, L, e.key === 'ArrowUp' ? 1 : -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [size, ladder.sizes.length, setSize]);

  // rise in on mount (whatever is inside takes focus for itself)
  useEffectC(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setEnter(true)));
    const t1 = setTimeout(() => setRim('fading'), 1600); // let the entry play
    const t2 = setTimeout(() => setRim('off'), 2900); // 1.6s hold + 1.3s fade
    return () => {clearTimeout(t1);clearTimeout(t2);};
    // eslint-disable-next-line
  }, []);

  // animate the sheet out, then run the action
  const exit = (fn) => {
    setLeaving(true);
    if (onExit) onExit();
    setTimeout(fn, 300);
  };

  return { size, setSize, sizes: ladder.sizes, full: size === 'full',
    /* `full` is the RUNG; `bleed` is whether that rung reaches the screen's
       edges, and since full grew the window's gutter those are two different
       questions on a desktop. Everything about the sheet's DRESS asks the
       second one — see mdSheetBleeds. */
    bleed: window.mdSheetBleeds(size, L),
    /* On the screens whose ladder has one rung — a phone, a phone sideways, an
       iPad held sideways — the sheet IS the screen, and it RISES from the
       bottom, which is the gesture the "+" implies. Where the ladder has more
       than one rung the sheet is a thing laid ON the app rather than a
       replacement for it, so it lifts and fades in place instead: a card that
       shot up from the bottom edge and then stopped two thirds of the way would
       be a promise the frame does not keep. */
    rises: ladder.sizes.length === 1,
    /* The screen itself travels with the stand (CS-11): where the Info Panel
       may dock is a question about the rung AND the shape, and the tenant that
       asks it should not have to call `useMdLayout` a second time to get an
       answer this hook already holds. */
    layout: L,
    frame: window.mdSheetFrame(size, L), enter, leaving, rim, exit };
}

function MdSheetFrame({ light, accent, stand, onClose, closeLabel,
  role = null, ariaLabel = null, attrs = null, after = null, inertBackground = false,
  aside = null, asideOverlay = false, onAsideAway = null,
  onDetails = null, detailsOn = false, foot = null, children }) {
  const { size, sizes, setSize, bleed, rises, frame, enter, leaving, rim } = stand;
  const hostRef = useRefC(null);
  const scrimRef = useRefC(null);

  /* EVERYTHING BEHIND A MODAL SHEET STOPS ANSWERING. Without this, Tab walks
     straight out of the sheet and into the app underneath it, and a screen
     reader reads the board as though the sheet were not there. Both of this
     frame's own nodes are exempt — the scrim included, because it is the
     surface that catches "put this away" and an inert scrim is a scrim that
     silently does nothing.

     THE WINDOWS' MECHANISM, not a second one that agrees with it by hand. This
     frame kept its own copy of that walk, and two writers marking the same
     nodes on their own reasoning is how a page ends up inert with nobody able
     to wake it — the whole story is in md-database.jsx. One stack decides now,
     and the sheet is simply the layer standing highest in it. */
  useDbInertBackgroundC([hostRef, scrimRef], inertBackground);

  /* FOCUS COMES BACK TO WHERE IT WAS. Opening moves it into the writing — the
     editor does that itself, for instant capture — and closing used to leave it
     on <body>, so the next Tab started at the top of the document and anyone
     who opened this from the keyboard had to walk back to where they were.
     Portalling the sheet out of the app makes that worse rather than better:
     there is no longer even a DOM neighbourhood for focus to fall into. An
     opener that has left the document — a card that was deleted, a FAB whose
     menu closed with it — is not chased.

     Taken on the FIRST RENDER and not in an effect, because effects run after
     the editor has already taken focus: what would be remembered as "where you
     came from" is the editor itself, a node about to be unmounted, so nothing
     at all would be restored.

     IT LIVES IN THE FRAME, DIRECTLY BELOW THE SILENCE, AND THAT ORDER IS THE
     WHOLE POINT. React tears a parent down BEFORE its child, so a restore that
     sat in the sheet above ran while this frame's `inert` was still up — and
     `focus()` into an inert subtree is ignored without a word, in every engine.
     Focus simply vanished to <body>. Declared here and second, the silence
     lifts first and the opener is reachable when it is called. The project
     sheet, which never had a restore of its own, gets one by standing in the
     same frame. */
  const openerRef = useRefC(null);
  if (openerRef.current === null) openerRef.current = document.activeElement;
  useEffectC(() => () => {
    const back = openerRef.current;
    if (back && back.isConnected && typeof back.focus === 'function') back.focus();
  }, []);
  /* Above every window, and OUT OF THE APP. The app root is `isolation:
     isolate`, so this plane is only a real plane once it is a child of <body> —
     see md-sheet-size.ts for the storeys. Fixed, because the frame it was
     handed is measured against the viewport. */
  return ReactDOM.createPortal(
    <React.Fragment>
    {/* THE SCRIM, and only where the sheet does not cover the screen anyway.
        It is what makes a sheet read as laid ON the app rather than lost in
        front of it, and it is the surface that catches a click meant for
        "put this away" — the same bargain every window here already keeps.

        The test is `bleed`, not the rung: full keeps the window's 22pt gutter
        now (Marwan, 2026-08-21), so on a desktop there is a margin of board
        showing all the way round even at the top of the ladder. Undimmed and
        unclickable, that margin was app you could see and could not use. Only
        where the sheet truly reaches the edges is there nothing behind to dim
        and nothing beside to click. */}
    {!bleed &&
    <div data-md-sheet-scrim="" ref={scrimRef} onPointerDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }} style={{ position: 'fixed', inset: 0, zIndex: MD_SHEET_SCRIM_Z,
      background: rgbaC('#000000', light ? 0.14 : 0.42),
      opacity: enter && !leaving ? 1 : 0, transition: 'opacity .34s ease' }} />}

    {/* `data-md-phone-viewport-layer` IS PORTED FROM main, WHICH PUT IT ON A
        ROOT THIS BRANCH RETIRED. On a phone `100vh` is the height the viewport
        WOULD have with Safari's bars collapsed, so a sheet measured against it
        is taller than the screen it stands on and its foot — the ✓ — sits under
        the toolbar. index.html answers that with `100dvh` for anything wearing
        the flag. CS-5 made the sheet its own fixed layer on <body>, so the div
        main tagged is gone and the flag belongs on the layer that replaced it.
        Only where the sheet reaches the edges: elsewhere the frame is a
        rectangle it computed, and the rule's `inset:0` would overrule it. */}
    <div data-md-sheet={size} ref={hostRef} {...(role ? { role, 'aria-modal': 'true' } : {})}
      {...(bleed ? { 'data-md-phone-viewport-layer': 'true' } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})} {...(attrs || {})}
      className={light ? 'md-db-light' : ''}
      style={{ position: 'fixed', zIndex: MD_SHEET_Z, '--db-accent': accent,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      background: light ? '#FFFFFF' : '#000000',
      /* The hairline and the shadow are the other half of "match the windows"
         — a rectangle with the window's geometry and none of its edge reads as
         a hole cut in the app rather than a card laid on it. Same test as the
         scrim: an edge drawn against the bezel and a shadow cast on nothing
         are what they were dropped for, and neither of those is true once
         there is board showing around the sheet. */
      border: bleed ? 'none' : `0.75px solid ${inkC(light ? 0.08 : 0.11)}`,
      boxShadow: bleed ? 'none' : `0 24px 70px ${rgbaC('#000000', light ? 0.18 : 0.55)}`,
      ...frame,
      ...(rises ? {
        transform: enter && !leaving ? 'translateY(0)' : 'translateY(100%)',
        transition: `transform .46s cubic-bezier(.32,1.02,.4,1), ${MD_FRAME_MOTION}`,
        willChange: 'transform',
      } : {
        opacity: enter && !leaving ? 1 : 0,
        transform: enter && !leaving ? 'translateY(0)' : 'translateY(16px)',
        transition: `transform .4s cubic-bezier(.32,1.02,.4,1), opacity .28s ease, ${MD_FRAME_MOTION}`,
        willChange: 'transform, opacity',
      }) }}>

      {/* the colorful rim — ceremony, then calm: it traces the frame's edge
          while the sheet rises in (the FAB's bloom completing itself), holds a
          moment, then dissolves and unmounts. */}
      {rim !== 'off' &&
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0,
        padding: 2.2, borderRadius: frame.borderRadius, zIndex: 6, pointerEvents: 'none', transform: 'translateZ(0)',
        background: 'conic-gradient(from var(--mdspin), #7C68E6, #60A5FA, #34D399, #FBBF24, #FB7185, #A78BFA, #7C68E6)',
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)', WebkitMaskComposite: 'xor',
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)', maskComposite: 'exclude',
        opacity: rim === 'on' ? 0.95 : 0, transition: 'opacity 1.3s ease',
        animation: 'mdSpin 9s linear infinite' }} />}

      <SheetHead light={light} accent={accent} onMinimize={onClose}
        closeLabel={closeLabel}
        onDetails={onDetails} detailsOn={detailsOn}
        size={size} sizes={sizes} onSize={setSize} />

      {/* ── THE ASIDE, IN BOTH ITS PLACEMENTS (CS-11 · D4) ─────────────────
          The frame owns this geometry rather than the tenant, and the reason
          is the row it opens: everything below the head shares one box, so
          BELOW THE HEAD is where an overlay has to be bounded. Placed by the
          tenant instead it was absolute against the whole sheet and covered
          the size control — which is the one control you reach for when a
          panel is overlaying and you would rather it docked.

          DOCKED it is a second column. The writing keeps `flex: 1 1 auto;
          min-width: 0`, so the panel's width comes out of the SLACK the frame
          had; at a rung wide enough to dock, what is left is still wider than
          the measure, which is exactly what mdInfoPanelPlacement proved before
          anything was drawn.

          OVERLAID it comes over the trailing edge of that same box, so it is
          clipped by the sheet's rounded corner and travels with it. The sliver
          of record still showing is the tap-away, and it is what says the
          panel is laid ON the record rather than instead of it.

          With no aside the children stay exactly the column they have always
          been, rather than every sheet gaining a wrapper it does not use.

          ── AND IT STOPS ABOVE THE FOOT (found by driving it) ──────────────
          `foot` is passed separately from `children` for one reason: this box
          is what the panel is laid over, and the ✓ must not be in it. It was —
          the assent sits at the trailing corner, which is precisely where a
          trailing panel lands — so opening Details covered the one control the
          sheet exists for, and on a NEW record it covered Create while leaving
          it drawn and enabled. Rule 1, with the button visible underneath a
          scrim so it read as disabled rather than hidden.

          The panel describes the record. The ✓ makes it. A description does
          not stand on the act, so the act is a sibling of the row, spanning
          the sheet under both columns, at every rung and in both placements. */}
      <div data-md-sheet-body="" style={{ flex: 1, minHeight: 0, display: 'flex',
        flexDirection: 'column' }}>
      {aside
        ? <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row',
          position: 'relative' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0, minHeight: 0,
            display: 'flex', flexDirection: 'column' }}>{children}</div>
          {asideOverlay
            ? <React.Fragment>
              <div aria-hidden="true" onPointerDown={() => onAsideAway && onAsideAway()}
                style={{ position: 'absolute', inset: 0, zIndex: 9,
                  background: rgbaC('#000000', light ? 0.1 : 0.34) }} />
              <div style={{ position: 'absolute', insetBlock: 0, insetInlineEnd: 0, zIndex: 10,
                display: 'flex', flexDirection: 'column', paddingTop: 8,
                width: `min(${MD_INFO_PANEL_W}px, calc(100% - 44px))`,
                animation: 'mdSlotIn .18s ease',
                background: light ? '#FFFFFF' : '#000000',
                borderInlineStart: `0.75px solid ${inkC(light ? 0.08 : 0.13)}`,
                boxShadow: `-18px 0 44px ${rgbaC('#000000', light ? 0.1 : 0.5)}` }}>{aside}</div>
            </React.Fragment>
            /* The shell grows; the panel inside keeps its width and is
               revealed. Both halves of B5 at once: the writing beside it
               glides rather than jumping, and nothing inside the panel
               reflows while it arrives. */
            : <div style={{ flex: 'none', display: 'flex', overflow: 'hidden',
              width: MD_INFO_PANEL_W,
              animation: 'mdPanelDock .34s cubic-bezier(.22,.8,.25,1)' }}>{aside}</div>}
        </div>
        : children}
      </div>
      {foot}
    </div>

    {/* Beside the frame rather than inside it: a confirmation is not part of
        the sheet's scroll and must not be clipped by its rounded edge.

        THE STOREY IS THE TENANT'S NOW (Marwan, 2026-08-24). This used to wrap
        `after` in a full-screen box at MD_SHEET_ASK_Z, because ConfirmDialog
        drew itself at a z-index of its own invention and a question asked from
        a sheet at 151 was painted underneath it. That fixed the one tenant who
        had a landlord — the Desks screen, asking the same question with no
        frame around it, buried its own the same way. The dialog hosts on
        <body> at the plane its registry row names, so the wrapper has nothing
        left to lift; kept, it would be an invisible fixed sheet of glass over
        the whole screen, swallowing every pointer meant for the dialog. */}
    {after}
    </React.Fragment>, document.body);
}

/* ── the sheet itself ── */
/* `mode` used to be a prop, and there is nothing left for it to decide: a new
   record and an existing one are now the same object in the same store from the
   moment either is opened, so this sheet does not need to know which it is. */
/* `project` turns this into a PROJECT'S MAIN RECORD — the same sheet, because a
   project is a record and there is no second editor in this app. What differs is
   only where the writing goes and what three controls mean:

     • the text is `md:project:{id}` rather than an element in a day. A project
       has no day, so there is no schedule to keep — see md-record.ts on one
       species and two homes;
     • line one is still the title, and here the title IS the project's name, so
       renaming a project is editing its first line like any other record;
     • When becomes the TARGET DATE (a day, no time), the identity dot moves the
       project between parts of life, and More → Delete deletes the project.

   Everything else — the rise-in, the rim, the measure, the toolbar, the keyboard
   handling — is untouched and shared, which is the whole reason this is a mode
   rather than a second sheet that would drift away from this one. */
/* ── the record's embeds, drawn where they were written ──────────────────────
   A link on a line of its own is shown as the thing it points at, in the place
   the writer put it — above this paragraph, below that one, two of them if they
   wrote two. That is md-markdown.jsx's slot-and-overlay arrangement doing the
   work: the editor reserves a box on the line and this draws into it from a
   layer above the contentEditable, so an <iframe> is never inside the subtree
   that gets rewritten on every keystroke. A video that restarts when you type
   is worse than no video at all, and that constraint is why this used to sit in
   a strip UNDER the whole editor, disconnected from the line it belonged to.

   Two functions, and both are asked per line:
     blockOf     what box to reserve, before anything has loaded
     renderBlock what to draw in it, and what its controls do

   Neither knows anything about media: md-embed.ts reads the line and md-embed.jsx
   draws whatever came back. */


function ComposeSheet({ accent, light, initial, initialDone, initialPid = null, fresh = false, timeBinding, dayOffset, nowMin, onLive, onClose, onDiscard, showAnchors = true, weekStart = 'Sat', backlog = false, onBacklogSchedule = null, initialStatus = null, onStatusSeed = null, project = null, onProjectGone, wikiRecords = [], wikiBacklinksFor = null, onWikiOpen = null, onWikiCreate = null, tagOptions = [], bookRefKey = null, template = null, onRecordStatus = null }) {
  const proj = project && window.mdProjectById ? window.mdProjectById(project) : null;
  const isProject = !!proj;
  /* ── DOES THIS SHEET ASK FOR A NAME? (P2) ────────────────────────────────
     A NEW record does, on its own line, exactly as the beginning asks for a
     project's name — because the two sheets are one surface now and a caret
     blinking in an empty box is not the same invitation as a labelled field.

     An EXISTING one does not, and that is a decision rather than an omission:
     it is first-line-is-the-title, the way a project reopened in this sheet
     already is. Storage never learns the difference — a record has always kept
     its name and its body as two fields (md-record.ts), so what changes here
     is only which control fills the first of them. */
  const named = fresh && !isProject;
  const [name, setName] = useStateC('');
  const nameRef = useRefC(null);
  // ONE text — line 1 is the title (Apple-Notes style), the rest is the body.
  const [text, setText] = useStateC(() => isProject
    ? (window.mdProjectTextRead(project) || window.mdProjectSeedText(proj.name))
    /* mdEmbedLegacy folds a pre-embed record's `file` field into its writing —
       see md-embed.ts. It is a no-op for every record written since, and the
       first save after opening one of the old ones makes the fold permanent:
       `collect` no longer carries the field, so the line is what is left. */
    : window.mdEmbedLegacy(seedC(initial), initial && initial.file));
  const [links, setLinks] = useStateC(() => isProject
    ? (window.mdProjectLinksRead ? window.mdProjectLinksRead(project) : [])
    : (window.mdWikiBindings ? window.mdWikiBindings(initial && initial.links, seedC(initial)) : []));
  /* The record's identity, fixed for as long as this sheet is open. It has to be
     a ref: `collect` now runs on every keystroke, and a uid minted inside it
     would make each character a NEW record — a day filling with one card per
     letter. md-app supplies one for an existing record; the fallback is for a
     caller that opens the sheet without one. */
  const uidRef = useRefC(initial && initial.uid ? initial.uid : uidC());
  const tagEditorRef = useRefC(null);
  const sourceRef = isProject ? { type: 'project', id: project }
    : { type: window.mdRecordType(initial), id: uidRef.current };
  const availableWikiRecords = (wikiRecords || []).filter((row) =>
    window.mdWikiTargetKey(row.ref) !== window.mdWikiTargetKey(sourceRef));
  const backlinks = wikiBacklinksFor ? wikiBacklinksFor(sourceRef) : [];
  const outgoing = window.mdWikiLinks ? window.mdWikiLinks(text, links).filter((row) => row.target).map((row) => {
    const target = availableWikiRecords.find((item) => window.mdWikiTargetKey(item.ref) === window.mdWikiTargetKey(row.target));
    return { ...row, title: target ? target.title : row.label, subtitle: target ? target.subtitle : 'Record unavailable' };
  }) : [];
  const unresolved = window.mdWikiLinks ? window.mdWikiLinks(text, links).filter((row) => !row.target) : [];
  const activeTags = window.mdTags ? window.mdTags(text) : [];
  /* A project document writes straight to its own store, so the app-wide index
     may be one render behind while its sheet is still open. Keep newly created
     local tags available immediately; the derived catalog takes over as soon
     as the surrounding record state catches up. */
  const tagChoices = [...(tagOptions || [])];
  for (const tag of activeTags) if (!tagChoices.some((row) => row.key === tag.key)) {
    tagChoices.push({ ...tag, count: 1, records: [] });
  }
  /* Which project this record belongs to, if any. `initialPid` lets a caller open
     the sheet already joined — that is how "New record" inside a project works,
     and it is why joining is never a step you have to remember afterwards. */
  const [pid, setPid] = useStateC(initial && initial.pid ? initial.pid : (initialPid || null));
  /* A record that arrives already joined arrives with its category settled, because
     a project owns its domain. Deriving this only for DISPLAY was a bug: the dot
     drew the right colour while `domain` stayed null, so the picker offered "pick
     a category first" next to a category that was plainly already chosen. */
  const seedProject = () => {
    const seedPid = (initial && initial.pid) || initialPid;
    return seedPid && window.mdProjectById ? window.mdProjectById(seedPid) : null;
  };
  const [domain, setDomain] = useStateC(() => {
    if (isProject) return proj.domainId;
    if (initial && initial.domain) return initial.domain;
    const p = seedProject();
    return p ? p.domainId : null;
  });
  /* A project's OWN record is never locked: the dot is how a project moves
     between parts of life, and a control that cannot be honoured is worse than
     no control at all. A record joined TO a project still is, because there the
     project owns the answer. */
  const [domainLocked, setDomainLocked] = useStateC(() =>
    !isProject && ((initial && !!initial.domain) || !!seedProject()));
  const [markDone] = useStateC(!!initialDone); // seeded from the record; toggled from the day list, not here
  // unified schedule for this record: which day, all-day vs timed, and the window.
  //   • grid record  → arrives timed at the drawn slot
  //   • plain “+”     → all-day, today, until the picker says otherwise
  //   • editing       → derived from the existing record
  const [sched, setSched] = useStateC(() => {
    /* A project's main record carries a TARGET, not a schedule: a day and no
       time of day, because a project aims at the 19th rather than at 14:30 on
       the 19th. Stored as a day key, held here as an offset only while the
       picker is open. A project with none yet opens on the same default a new
       project gets, so the two can never disagree about the usual distance. */
    if (isProject) {
      const cur = window.mdProjectTarget ? window.mdProjectTarget(proj) : null;
      const off = mdOffsetForKey(cur);
      return { offset: off != null ? off : (window.MD_PROJECT_TARGET_DAYS || 40) };
    }
    const o = dayOffset || 0;
    if (timeBinding) return { offset: o, min: timeBinding.min, dur: timeBinding.dur };
    if (initial && initial.min != null) return { offset: o, min: initial.min, dur: initial.dur || 30 };
    // Every record is timed now. A capture with no stated time is a thing you
    // are doing NOW, so the default start is the minute the sheet opened —
    // snapped to 5 so it lands on the ribbon's grid. Editing a legacy all-day
    // record (min == null) picks up the same default, which is what promotes it.
    return { offset: o, min: mdSnap5(nowMin != null ? nowMin : window.mdNowMin()), dur: 30 };
  });
  const [whenOpen, setWhenOpen] = useStateC(false);
  const [whenDraft, setWhenDraft] = useStateC(null);
  /* A day chosen for a record that does not exist yet cannot be acted on when
     it is chosen — there is no row in the backlog to lift out of it. So the
     choice is remembered here and spent by Create, which does both acts in
     order. (P-7) */
  const [scheduleOnCreate, setScheduleOnCreate] = useStateC(false);
  const [confirmDelete, setConfirmDelete] = useStateC(false);

  /* ── HOW BIG THIS SHEET IS, WHICH IS NOW ITS OWN QUESTION (CS-5) ─────────
     It used to be the host's: the root was `position: absolute; inset: 0`, so
     the sheet was whatever box md-app.jsx nailed it into, and one boolean —
     a boolean passed by whichever of the TWO mount sites had rendered it —
     chose between two dresses. The sheet asks now, of the same `useMdLayout()`
     everything else asks, and gets back a ladder (md-sheet-size.ts).

     CS-8 moved the asking into `useMdSheetStand`, above, because a project's
     beginning asks exactly the same question and got a different answer for as
     long as the two had separate frames. The hook's one argument was how this
     sheet closed the toolbar's picker on the way out; the toolbar is gone, and
     every picker left on this sheet is a portalled `NpMenu` that closes itself
     against its own anchor. */
  const stand = useMdSheetStand();
  const { size, exit } = stand;

  // the software keyboard's inset — the toolbar rides on top of it, Apple-Notes
  // style, so When · tools · Save stay visible while typing. visualViewport
  // shrinks when the keyboard opens; on desktop it never does and kb stays 0.
  // The software keyboard's inset. The toolbar does NOT ride on top of it —
  // it stays pinned to the foot of the record and the keyboard covers it, which
  // is the honest arrangement: a bar that jumps whenever the keyboard opens or
  // closes is more motion than the two controls on it are worth. The inset is
  // spent on the EDITOR instead, as bottom padding, so its own overflow can
  // always scroll the caret out from under the keyboard. Desktop: stays 0.
  const [kb, setKb] = useStateC(0);
  useEffectC(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onR = () => {
      setKb(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
      // iOS scrolls the window while it presents the keyboard, which pushed the
      // whole record half off screen (black void above the keyboard). The record is
      // full-screen — there is never anything legitimate to scroll to. Pin it.
      if (window.scrollY || vv.offsetTop) window.scrollTo(0, 0);
    };
    vv.addEventListener('resize', onR);
    vv.addEventListener('scroll', onR);
    onR();
    return () => {vv.removeEventListener('resize', onR);vv.removeEventListener('scroll', onR);};
  }, []);

  /* AI inference — debounced; stays neutral until confident; never overrides a
     manual pick. It used to drive a sparkle inside the toolbar's dot while it
     ran; the tile it fills now is the beginning's, which says nothing until it
     has something to say, so the "thinking" state went with the toolbar. */
  const guessFrom = named ? `${name}\n${text}` : text;
  useEffectC(() => {
    /* Never on a project's own record. Its part of life is a STORED FACT, not a
       guess to be re-made from the writing — and it is not `domainLocked`
       either, because that would disable the tile the writer changes it with.
       Two different questions that used to share one flag. */
    if (domainLocked || isProject) return;
    const t = guessFrom.trim();
    if (t.length < 3) {setDomain(null);return;}
    const id = setTimeout(() => setDomain(mdInferDomain(t)), 600);
    return () => clearTimeout(id);
  }, [guessFrom, domainLocked]);

  const pickDomain = (id) => {
    // A project always belongs somewhere: "Auto" is not offered on its record,
    // and clearing the domain here would leave one that belongs nowhere.
    if (id == null && isProject) return;
    /* ── AND PICKING ONE LEAVES THE PROJECT ────────────────────────────────
       A project OWNS its domain (md-project.ts), so the two cannot both be
       true: `collect` asks `mdProjectDomainFor` and the project's answer wins,
       which would make this tile a control that draws one thing and saves
       another — rule 1's exact prohibition.

       The band solved that by refusing to be a control at all while a project
       was set. The tile cannot: it is the beginning's, and there it is always
       pressable. So it does what the old toolbar's picker did and what the
       beginning does with a pursuit — it leaves. Visibly: the crumb beside it
       says "No project" the moment it happens, so this is a choice you watch
       rather than a rule you discover. */
    if (id != null && pid) setPid(null);
    if (id == null) {setDomainLocked(false);setDomain(mdInferDomain(guessFrom.trim()));} else
    {setDomain(id);setDomainLocked(true);}
  };

  /* Joining a project also settles the category, because a project owns its
     domain — and it LOCKS it, so the classifier cannot quietly re-guess a record
     whose home you already stated. Leaving one hands the record back to the
     guess, exactly as "Auto" does. */
  const pickProject = (id) => {
    setPid(id);
    const project = id && window.mdProjectById ? window.mdProjectById(id) : null;
    if (project) {setDomain(project.domainId);setDomainLocked(true);} else
    {setDomainLocked(false);setDomain(mdInferDomain(guessFrom.trim()));}
  };

  /* Create and join in one gesture. The domain is not asked for: by the time
     this is reachable the record already has one, either picked or inferred from
     what has been written. */
  const createProject = (title, domainId) => {
    if (!window.mdProjectCreate) return null;
    const made = window.mdProjectCreate(title, domainId);
    if (made) pickProject(made.id);
    return made;
  };

  /* ── WHAT THIS RECORD IS CALLED, ASKED ONCE ──────────────────────────────
     Two sources, one answer: the field where there is one, the first line
     where there is not. Everything that needs the title — the commit, the
     delete confirmation, an embed's caption — reads THIS, so a sheet cannot
     save under one name and ask about another. */
  const recordName = named ? name.trim() : partsC(text).name;

  const collect = () => {
    // line 1 → the name, the rest → the body. md-record.ts owns that split, and
    // the day list titles its cards with the same module's answer.
    /* …unless the sheet ASKED for the name (P2), in which case the split has
       already happened in the person's own hands and re-deriving it from the
       writing would throw their answer away. Same two fields either way. */
    const parts = { name: recordName, body: named ? text : partsC(text).body };
    const base = {
      uid: uidRef.current,
      /* A project owns its domain, so md-project.ts answers this rather than the
         sheet — one place decides, and the two facts can never disagree on a
         stored record. */
      pid: pid || null,
      domain: (window.mdProjectDomainFor ? window.mdProjectDomainFor(pid, domain) : domain) || null,
      name: parts.name,
      body: parts.body,
      created: initial && initial.created ? initial.created : Date.now(),
      /* Carried BY NAME because this sheet rebuilds the record rather than
         spreading it — scheduling a backlog record comes through here, and a
         dropped origin would silently re-date it to the day it was scheduled. */
      origin: (initial && window.mdRecordOriginDay(initial)) || window.mdRecordOriginStamp(),
      offset: sched.offset
    };
    if (links.length) base.links = links;
    /* No `file` here, and its absence is deliberate rather than an oversight:
       the document is a LINE OF THE WRITING now (see md-embed.ts), so it is
       already in `parts.body` and rebuilding the record cannot drop it. A record
       that still carries the old field had it folded into the text when this
       sheet opened, so saving is what completes the move. */
    // Always an event-shaped block carrying its note, landing in that day's
    // timeline. There is no untimed branch any more.
    return { ...base, kind: 'event', record: true, allDay: false, id: domain || undefined,
      min: sched.min, dur: sched.dur, t: mdMinToHHMMc(sched.min),
      loc: initial && initial.loc || undefined };
  };

  /* ── the record, live ────────────────────────────────────────────────────────
     An EXISTING record is saved as it is written: this runs on every change, so
     the card's title IS the first line, tracking the caret rather than
     following it. That is what the sheet has always done and it is not what
     changed.

     ── WHAT CHANGED, AND WHY (P-5, Marwan 2026-08-21) ────────────────────────
     It used to run on MOUNT too, for a new record — which is what put a
     placeholder card in your day the instant "+" was tapped, timed as if the
     record had already been written. It committed a BLANK row on purpose, on
     the argument that the card has to be there while you decide what to write,
     and md-app dropped it again if you closed without writing anything.

     The argument was sound and the arrangement was still wrong, because the
     sheet beside it asks. A project writes nothing until Create; a record minted
     itself before you had decided you wanted one, and backing out was something
     the app did for you, quietly. Two identical-looking surfaces committing by
     opposite rules is the duplication this phase exists to end, one level below
     where it stopped.

     So a NEW record holds a draft, exactly as a beginning does, and `begin`
     below is the one write. Nothing is lost: what the placeholder card gave you
     was the sight of the thing you were making, and the sheet is now that
     sight — the crumb, the name, the pills and the day, all on screen while you
     decide. */
  useEffectC(() => {
    /* `named` is the sheet that ASKED, which is exactly the sheet whose subject
       does not exist yet. A record you opened is written as you write it. */
    if (named) return;
    /* A project's main record commits to its own document instead of into a
       day, and its first line is the project's NAME — so writing the title is
       what renames the project. An empty first line is left alone rather than
       blanking the name: mdProjectRename refuses it, and a project the writer
       is midway through re-titling must not vanish from the list. */
    if (isProject) {
      window.mdProjectTextWrite(project, text, links);
      const nm = partsC(text).name;
      if (nm) window.mdProjectRename(project, nm);
      if (domain && domain !== proj.domainId && window.mdProjectSetDomain) {
        window.mdProjectSetDomain(project, domain);
      }
      if (window.mdProjectSetTarget) window.mdProjectSetTarget(project, window.mdDayKey(sched.offset || 0));
      return;
    }
    if (onLive) onLive(collect(), markDone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    /* `pid` is in this list, and it had to be added (CS-7). The commit watched
       the domain and not the project, which looked right for as long as the
       only door to a project was the toolbar's dot — joining through that door
       sets the domain too, so the effect always had something to see. The
       properties band is a door the dot never was: move a record between two
       projects in the SAME part of life and the domain does not change, so
       nothing was written, the sheet drew the new project, and the next reload
       put the old one back. */
  }, [text, name, links, pid, domain, sched.offset, sched.min, sched.dur, markDone]);

  /* The name is the first question, so it is the first thing focused — the
     beginning's own line (md-projects.jsx), and rule 4: opening moves focus in.
     Where there is no name field the editor autofocuses itself instead. */
  useEffectC(() => { if (named && nameRef.current) nameRef.current.focus(); }, []);

  // On an EXISTING record the ✓ and the minus do the same thing: leave. Nothing
  // is being decided — it was saved as it was written.
  const doClose = () => exit(onClose);

  const [confirming, setConfirming] = useStateC(false);

  const dayLabel = (() => {
    const o = sched.offset || 0;
    /* A backlog record HAS no day, and the button says so instead of wearing
       today by default — "Choose its time" is the ask, in the record's own
       words (OD-3). Choosing one is what schedules it.

       …until P-5, which made the choosing and the scheduling two moments. The
       ask used to be the pill's only backlog state because confirming a day
       took the record out of the backlog on that press and reopened it as a day
       record — the pill never survived the choice. It does now, so a day that
       has been chosen and not yet spent has to be NAMED here; a pill still
       asking for the day you just picked is the app forgetting in front of
       you. Falls through to the record's own words below. */
    if (backlog && !scheduleOnCreate) return 'Choose its time';
    /* A target is a DATE and never a countdown — no "in 40 days", nothing that
       falls, nothing that turns red when it passes. See md-project.ts's header
       for why that distinction is load-bearing rather than stylistic. Relative
       words are dropped here too: "Today" is right for a plan you are writing
       now and wrong for one you are aiming at. */
    if (isProject) {
      const dt = window.mdDateForOffset ? window.mdDateForOffset(o) : null;
      if (!dt) return '';
      const thisYear = window.mdDateForOffset(0).year;
      return dt.year === thisYear ? `${dt.d} ${dt.mon}` : `${dt.d} ${dt.mon} ${dt.year}`;
    }
    if (o === 0) return 'Today';
    if (o === 1) return 'Tomorrow';
    if (o === -1) return 'Yesterday';
    const dt = window.mdDateForOffset ? window.mdDateForOffset(o) : null;
    return dt ? `${dt.d} ${dt.mon}` : '';
  })();
  /* A project has no window. The When button's second half is a record's time,
     and a target has none — so it is empty rather than filled with the word
     "Target", which the picker's own title and the button's label already say.
     The button then reads "19 Sep", the way a record's reads "Today · 8:15 PM". */
  const schedText = isProject || backlog ? '' : winLabelC(sched.min, sched.dur);

  /* ── THE PILL KIT, READ RATHER THAN REDRAWN (P-2) ────────────────────────
     md-projects.jsx publishes the beginning's own controls on `window.MdNp`
     and this file mounts them. It loads AFTER this one (index.html), so they
     are read here at render time — the same door `ScheduleOverlay`,
     `MdInfoPanel` and `BookReviewSection` already come through.

     Guarded because a sheet must still draw where md-projects has not loaded:
     several suites mount this component alone to ask about the frame. What is
     missing then is the pills, never the writing. */
  const Np = window.MdNp || {};
  /* One measure for the crumb, the form, the writing and the ✓ — the
     beginning's own function over this file's own number, so the day either
     one moves they move together. */
  const rail = Np.npRail ? Np.npRail()
    : { width: '100%', maxWidth: MD_MEASURE, marginInline: 'auto', boxSizing: 'border-box' };

  /* ── THE COLUMN'S STATUS, BEFORE THERE IS A RECORD TO WEAR IT (CS-10) ────
     A sheet opened from a board column describes a record the store has not
     heard of yet — OD-3 mints nothing — so the band has no stored status to
     read and would say "Inbox" over a + pressed under Planned. The column's
     word is carried here instead, and it is a SEED rather than a display
     trick: pick a different one on the blank sheet and that is what gets
     written, because a choice silently overwritten at the first keystroke is
     the one thing both write laws forbid. Once the row exists it speaks for
     itself and the seed is never consulted again.

     ── AND P-5 MADE THAT EVERY NEW SHEET, NOT ONLY A COLUMN'S ────────────────
     This was written for the one door that minted nothing. Now none of them do:
     a sheet that ASKED for a name is holding a draft, so there is no row to
     carry a status and the pill's answer has to be held here until Create.
     The rule below is that question asked plainly — does the store know about
     this record yet — rather than a list of the doors that happen to be new. */
  const [seedStatus, setSeedStatus] = useStateC(initialStatus);
  const backlogRowExists = !!(backlog && window.mdProjectRecordById
    && window.mdProjectRecordById(uidRef.current));
  const exists = named ? false : (backlog ? backlogRowExists : true);
  const pendingStatus = exists ? null : seedStatus;
  const seedPick = (id) => { setSeedStatus(id); if (onStatusSeed) onStatusSeed(id); };
  /* One writer, and it is not this file — md-props.jsx's own rule, kept word
     for word now that the pill has moved onto the sheet. A record the store has
     never heard of goes back up as a SEED; a real one goes through md-app's
     door, which re-reads the day state it holds; with neither, md-project.ts
     answers for itself. */
  const schedDay = () => (window.mdDayKey ? window.mdDayKey(sched.offset || 0) : null);
  /* The status is DERIVED — read out of `md:recordStatus` on every draw rather
     than held here, so a sheet and a board looking at one record can never
     disagree about where it stands. The cost of deriving is that a write React
     did not make is a write React does not know about, so the pick asks for one
     more draw and the engine's own answer is what gets read. */
  const [, bumpStatus] = useStateC(0);
  const writeStatus = (id) => {
    if (!exists) { seedPick(id); return; }
    if (onRecordStatus) {
      onRecordStatus({ uid: uidRef.current, record: collect(), scheduled: schedDay() }, id);
      bumpStatus((n) => n + 1);
      return;
    }
    if (window.mdRecordTrySetStatus) window.mdRecordTrySetStatus(collect(), id, schedDay());
    bumpStatus((n) => n + 1);
  };
  const openWhen = () => { setWhenDraft({ ...sched }); setWhenOpen(true); };

  /* ── THE TWO LAWS, IN THREE CONTROLS (P-5) ───────────────────────────────
     What the sheet held when it opened. A seed from the door it came through,
     and the defaults the pills SHOW, are not things a hand filled in — so
     leaving them behind costs nothing and asks nothing. Everything measured
     against this first render, which is the beginning's own arithmetic. */
  const opened = useRefC(null);
  if (opened.current === null) opened.current = { pid, domain, seedStatus,
    offset: sched.offset, min: sched.min, dur: sched.dur };
  const dirty = named && (!!name.trim() || !blankC(text)
    || pid !== opened.current.pid || domain !== opened.current.domain
    || seedStatus !== opened.current.seedStatus
    || sched.offset !== opened.current.offset || sched.min !== opened.current.min
    || sched.dur !== opened.current.dur);
  /* A sheet holding somebody's writing does not vanish on one keystroke. The ×
     and Escape both come through here: empty, it just closes; filled, it asks
     first — through the app's own ConfirmDialog, whose word for keeping is not
     "Cancel" on a sheet where Escape was the gesture being confirmed. */
  const attemptClose = () => {
    if (dirty && window.ConfirmDialog) setConfirming(true); else doClose();
  };
  /* THE ONE WRITE. One press, one gesture, one undo step — and dead until there
     is a name, exactly as the beginning's Create is. `markDone` rides along
     because md-app has always taken it here; a record being created is never
     already done, so it is false in practice and carried for shape. */
  const ready = named && !!name.trim();
  const begin = () => {
    if (!ready) return;
    const row = collect();
    if (onLive) onLive(row, markDone);
    /* The status the sheet has been HOLDING, applied the instant there is a row
       to wear it. A backlog record's is applied by md-app right after its own
       first write (CS-10) — `onStatusSeed` is what keeps the two in step — so
       writing it a second time here would be one door too many. */
    if (seedStatus && !backlog) {
      if (onRecordStatus) {
        onRecordStatus({ uid: uidRef.current, record: row, scheduled: schedDay() }, seedStatus);
      } else if (window.mdRecordTrySetStatus) {
        window.mdRecordTrySetStatus(row, seedStatus, schedDay());
      }
    }
    /* A NEW backlog record that was given a day is two acts in one press: it is
       written into the backlog, and then it is scheduled out of it. Under the
       old law the first had already happened at the first keystroke, so the
       When picker could do the second on its own; it cannot now, so it records
       the choice and this is where both happen, in order. md-app reopens the
       record as a day record, which is what closes the sheet. */
    if (backlog && scheduleOnCreate && onBacklogSchedule) {
      onBacklogSchedule(uidRef.current, window.mdDayKey(sched.offset || 0));
      return;
    }
    exit(onClose);
  };

  /* Escape peels one layer (rule 2). The schedule overlay owns it while it is
     open, and so do both questions; otherwise it is the way out — and on a new
     record the way out ASKS. */
  useEscape(() => (confirming ? setConfirming(false) : attemptClose()),
    !whenOpen && !confirmDelete);

  /* ── THE INFO PANEL (CS-11) ──────────────────────────────────────────────
     One panel, two placements, decided by arithmetic rather than taste — see
     mdInfoPanelPlacement. Docked it is a second column of the sheet and costs
     the writing nothing; overlaid it comes over the trailing edge, where it
     costs nothing either because it is temporary and a click puts it away.

     The rung can change underneath it, so the placement is DERIVED on every
     draw rather than captured when the panel opened: grow a popup to full with
     the panel up and it docks, shrink it back and it overlays, with no state to
     keep in step. Escape peels it before it peels the sheet (rule 2), which is
     what the layer stack is for. */
  const [panelOpen, setPanelOpen] = useStateC(false);
  const panelPlace = window.mdInfoPanelPlacement
    ? window.mdInfoPanelPlacement(stand.size, stand.layout) : 'overlay';
  (window.useDbEscapeLayer || (() => {}))(() => setPanelOpen(false), panelOpen, null, 'menu');
  const panelProps = {
    record: collect(), accent: rgbaC(accent, 1),
    scheduled: window.mdDayKey ? window.mdDayKey(sched.offset || 0) : null,
    onPickProject: pickProject, onPickDomain: pickDomain, onRecordStatus,
    pendingStatus, onPendingStatus: seedPick,
    dayLabel, schedText, whenLabel: isProject ? 'Target' : 'Schedule', onWhen: openWhen,
    linkGroups: { outgoing, backlinks, unresolved }, onOpenLinked: onWikiOpen,
    tagState: { active: activeTags, options: tagChoices, sourceRef },
    onTagAdd: (word) => tagEditorRef.current && tagEditorRef.current.add(word),
    onTagRemove: (word) => tagEditorRef.current && tagEditorRef.current.remove(word),
    /* ── DELETE, WHERE THE RECORD IS DESCRIBED (P-6) ────────────────────────
       The foot toolbar's More menu was its only home, and the toolbar is gone.
       It lands at the FOOT of the panel — after every section, below the facts
       it is about to remove — rather than as a seventh property row, because
       it is not a fact about the record, it is the end of one.

       Offered only where there is something to delete. A new record has not
       been created, so deleting it would be deleting nothing; the way out of
       that sheet is the × in the head, and the × already says so (rule 1). */
    onDelete: named ? null : () => setConfirmDelete(true),
    deleteLabel: isProject ? 'Delete project' : 'Delete record',
  };
  const panelOn = panelOpen && !!window.MdInfoPanel;

  /* The record, in the frame every sheet in this app now stands in (CS-8).
     It carries the ladder, the rung, the scrim, the rim and the head; what is
     left below is the record itself.

     THE WAY OUT SAYS WHICH SHEET THIS IS (P-5). An existing record was saved as
     it was written, so leaving is leaving: a minus, and the word is "Minimize".
     A NEW one holds a draft that is written nowhere, so its way out is an ×,
     and the word says out loud that nothing is kept. Same corner, same size,
     opposite promise — a person should be able to read which law they are under
     from the one control they reach for by accident. */
  return <MdSheetFrame light={light} accent={accent} stand={stand}
    onClose={named ? attemptClose : doClose}
    closeLabel={named ? 'Close without keeping' : 'Minimize'}
    /* `aria-modal` was a claim this sheet did not keep: a screen reader was
       told nothing outside it existed while Tab walked straight out into the
       day. The pointer was already stopped — the scrim swallows the click
       wherever there is anything beside the sheet to click — so this is the
       keyboard hearing what the mouse already heard. (2026-08-21) */
    role="dialog" ariaLabel={isProject ? 'Project' : 'Record'} inertBackground
    aside={panelOn
      ? <window.MdInfoPanel {...panelProps} placement={panelPlace} /> : null}
    asideOverlay={panelPlace === 'overlay'} onAsideAway={() => setPanelOpen(false)}
    /* The head's own door to it (Marwan, 2026-08-21). The band carries one and
       the More menu carries one, and both are conditional — the band only on a
       parented record, the menu behind a press. The head is on every sheet at
       every rung, so the answer to "where do I see what this is" stops
       depending on which kind of record you happened to open. */
    onDetails={window.MdInfoPanel ? () => setPanelOpen(!panelOpen) : null}
    detailsOn={panelOn}
    /* ── THE ASSENT, AND NOTHING BESIDE IT (P3) ────────────────────────────
       The foot toolbar stood here: six controls, of which two were stubs
       promising a word and no function, one (Details) is on the head at every rung
       already, and two more — the day and the coloured identity dot — are the
       pills above, with their words attached. What is left is the ✓, against
       the SHEET's corner rather than the measure's, keeping the same
       appointment the beginning's Create keeps. The sixth was Delete, the only
       real one, and it moved to the Details panel (P-6) rather than evaporating.

       It is handed to the FRAME rather than drawn among the children because
       the panel is laid over the children, and the panel does not get to stand
       on this. See the note beside `data-md-sheet-body`.

       ── D5, IN ONE CONTROL, NOW SAID THE SAME WAY TWICE (P-5) ──────────────
       The same circle, the same corner, the same act of assent — and the word
       says which act it is. On a record that already exists it means "I am done
       looking at this", and it is never disabled, because the writing was
       committed as it was typed. On a NEW one it IS the write, and it is dead
       until there is a name.

       The name matters beyond the aria: the grid's own ✓ is on screen
       underneath whenever a record is opened from the schedule, and two buttons
       both answering to "Done" is a screen reader with nothing to choose by. */
    foot={<div style={{ flexShrink: 0, position: 'relative', zIndex: 11,
      minHeight: 46 + MD_SHEET_INSET }}>
      <MdSheetDone accent={accent}
        onPress={named ? begin : doClose} ready={named ? ready : true}
        label={named ? 'Create record' : 'Done — close record'} />
    </div>}
    /* The two questions this sheet can ask, and both are asked OUTSIDE the
       frame's own box — a question clipped by the sheet's rounded edge or by
       its scroller is a question you cannot answer.

       They cannot both be up: `confirming` belongs to a sheet whose subject
       does not exist yet, and `confirmDelete` to one whose subject does. */
    after={confirming && window.ConfirmDialog
      ? <window.ConfirmDialog light={light} accent={accent}
        title="Discard this beginning?"
        message="Nothing is saved until you create it — close now and what you filled in is gone."
        confirmLabel="Discard" cancelLabel="Keep writing"
        onConfirm={doClose} onCancel={() => setConfirming(false)} />
      : confirmDelete && window.ConfirmDialog
        ? <window.ConfirmDialog light={light} accent={accent}
          title={isProject ? 'Delete this project?' : 'Delete this record?'}
          message={isProject
            ? `Your records stay where they are, in their days. You have ${window.MD_TRASH_DAYS || 30} days to change your mind.`
            : (recordName ? `“${recordName}” will be removed from your day.` : 'This record will be removed from your day.')}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            setConfirmDelete(false);
            if (isProject) {
              if (proj.builtin) {
                window.mdProjectDelete(project);
                exit(() => (onProjectGone || onClose)());
                return;
              }
              const result = await window.mdTrashDelete(`project:${project}`);
              if (result && result.ok) exit(() => (onProjectGone || onClose)());
              return;
            }
            exit(onDiscard);
          }} />
        : null}>

        {/* ── THE CRUMB: WHERE THIS RECORD IS GOING ───────────────────────
            The beginning's own rail, saying a record's version of the same
            sentence. A project is born into a pursuit; a record is born into a
            PROJECT — so the pill on the left is Projects and the word on the
            right is New Record (Marwan's 2.2).

            OUTSIDE the scroller, like the band it replaces: this is where the
            record IS, and a fact that scrolls away with the writing is a fact
            you have to go looking for. */}
        <div style={{ flexShrink: 0, position: 'relative', zIndex: 8, padding: '0 22px' }}>
        {/* No minHeight. It had one — 30 — and the beginning's own rail has
            none, so the row stood 2pt taller and every row below it, name to
            rule to writing, sat 2pt lower than on the sheet this is a clone
            of. Invisible on its own and wrong side by side, which is the only
            way this sheet is ever going to be looked at. */}
        <div data-md-crumb="" style={{ ...rail, display: 'flex', alignItems: 'center',
          gap: 4 }}>
          {isProject
            /* A project does not belong to a project. What it belongs to is a
               pursuit, and that answer is the beginning's to change — this
               sheet is the project already made, so it states it. */
            ? <span style={{ color: inkC(0.6), fontSize: 12.5, fontWeight: 600 }}>
              {(() => {
                const pu = proj.pursuitId && window.mdPursuitById
                  ? window.mdPursuitById(proj.pursuitId) : null;
                return pu ? pu.name : 'No pursuit';
              })()}
            </span>
            : <RecordProject accent={rgbaC(accent, 1)} pid={pid} domain={domain}
              onPick={pickProject} onCreate={createProject} />}
          <span aria-hidden="true" style={{ color: inkC(0.3), fontSize: 12.5 }}>›</span>
          <span style={{ color: inkC(0.6), fontSize: 12.5, fontWeight: 600 }}>
            {isProject ? 'Project' : named ? 'New Record' : 'Record'}</span>
        </div>
        </div>

        {/* ── the open record — the beginning's body, over a record's facts.
                 The record scrolls itself; head and crumb never move. ── */}
        <div data-comment-anchor="03ffe29724-span-323-15" className="md-scroll"
          style={{ flex: 1, minHeight: 0,
          position: 'relative', zIndex: 7, padding: '10px 22px 0', overflowY: 'auto',
          // room to scroll the caret out from under the keyboard — the window
          // itself is pinned, so this scroller is what iOS moves to reveal it
          paddingBottom: kb,
          /* A column, so the writing can be told to take the whole of it. That
             is what makes the space under the last line part of the record —
             click it and the caret goes to the end — instead of a dead margin
             belonging to the scroller. See `fill` in md-markdown.jsx. */
          display: 'flex', flexDirection: 'column' }}>
          {/* The RECORD is edge to edge; the LINE is not. Prose set across a whole
              canvas is genuinely hard to read — the eye loses the line coming back
              from the right margin — so the writing column keeps a measure and
              centres in the record. Generous rather than narrow. In the panel this
              is inert: 780 is wider than the panel ever gets. */}
          <div style={{ ...rail, position: 'relative',
            flex: 1, display: 'flex', flexDirection: 'column' }}>

          {/* ── THE NAME, ON ITS OWN LINE (P2) ──────────────────────────────
              The beginning's name row exactly: the part-of-life tile, then a
              22pt field, then nothing else on the line. A NEW record only —
              see `named` above. */}
          {named &&
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {Np.NpDomain
              /* `pursuits={false}` — a record belongs to a project, never to a
                 pursuit, so the half of that menu offering "New pursuit…" is
                 not a question a record can be asked. Same tile, same colours,
                 same function; one half of the menu simply is not drawn. */
              ? <Np.NpDomain accent={rgbaC(accent, 1)} domainId={domain} pursuits={false}
                pursuitId={null} pursuitName={null}
                onPick={pickDomain} onPursuit={() => {}} />
              : null}
            <input ref={nameRef} dir="auto" value={name} aria-label="Record name"
              placeholder="Record name" onChange={(event) => setName(event.target.value)}
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent',
                color: inkC(1), font: 'inherit', fontSize: 22, fontWeight: 640,
                outline: 'none', padding: '2px 0' }} />
          </div>}

          {/* ── THE PROPERTIES, AS PILLS ────────────────────────────────────
              The beginning's row, over the three answers a record has: where
              it stands, when it runs, what it is about. No Priority, and that
              absence is rule 1 rather than an oversight — a record has no
              priority field anywhere to write one into. */}
          <div data-md-props="" style={{ display: 'flex', flexWrap: 'wrap', gap: 7,
            padding: named ? '14px 0 12px' : '2px 0 12px' }}>
            {isProject ? null
              : <RecordStatus accent={rgbaC(accent, 1)} record={collect()}
                scheduled={schedDay()} pending={pendingStatus} onPick={writeStatus} />}
            <RecordWhen accent={rgbaC(accent, 1)} label={isProject ? 'Target' : 'Schedule'}
              dayLabel={dayLabel} schedText={schedText} onOpen={openWhen} />
            {Np.NpTags
              ? <Np.NpTags accent={rgbaC(accent, 1)} options={tagChoices} text={text}
                onText={setText} />
              : null}
          </div>

          {/* What this record IS, ruled off from what it is ABOUT — on the
              RAIL rather than the sheet's own edge, because the rule belongs to
              the thing it is ruling. The beginning draws the same hairline at
              the same weight. */}
          <div data-md-rule="" aria-hidden="true"
            style={{ height: 1, background: inkC(light ? 0.07 : 0.09), margin: '0 0 12px' }} />
          {/* The project's part of life, before its title. It is drawn OUTSIDE the
              contentEditable on purpose: md-markdown.jsx maps caret offsets onto
              the text it rendered, and a node injected into the editor that
              contributes nothing to textContent is exactly what breaks that map.
              So the title line reserves room with text-indent and the icon is
              positioned into it — the editor's text is still only the text.

              The bottom of the tile sits on the title's baseline, which is what
              MD_LEAD_TOP is measured to. */}
          {isProject && domain &&
          <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: MD_LEAD_TOP,
            width: MD_LEAD_SIZE, height: MD_LEAD_SIZE, borderRadius: 9, overflow: 'hidden',
            display: 'grid', placeItems: 'center', pointerEvents: 'none',
            background: rgbaC(mdDomainColor(domain, light), light ? 0.12 : 0.16),
            border: `0.75px solid ${rgbaC(mdDomainColor(domain, light), light ? 0.28 : 0.34)}` }}>
            {mdDomainIcon(domain, mdDomainColor(domain, light), 15)}
          </span>}
          {/* no placeholder, no greyed hints — an open record and a caret, like a
                   fresh Apple Note. The label lives in aria only. */}
          {/* autoFocus only on a NEW record. Focus is what raises the software
                   keyboard, and on a record that already has writing in it that means
                   the keyboard covers half of what you opened it to read. Tapping
                   the text still focuses it, which is how you edit — the difference
                   is that the app has stopped assuming.

                   …and NOT on a sheet that asked for the name (P2): the field
                   above is the first thing to answer, so it is the first thing
                   focused. Two autofocused controls is one of them losing. */}
          <LiveMD value={text} onChange={setText} accent={rgbaC(accent, 1)}
          wikiLinks={links} wikiRecords={availableWikiRecords}
          onWikiLinksChange={setLinks} onWikiOpen={onWikiOpen} onWikiCreate={onWikiCreate}
          tagOptions={tagChoices} tagApiRef={tagEditorRef}
          firstLineTitle={!named} autoFocus={fresh && !named}
          ariaLabel={named ? 'What this record is'
            : `${fresh ? 'New record' : 'Record'} — first line is the title`}
          fontSize={16} fill
          onCreateDatabase={() => {
            const db = window.mdDatabaseCreate && window.mdDatabaseCreate('Untitled database');
            return db && window.mdDatabaseLine ? window.mdDatabaseLine(db.id) : '';
          }}
          blockOf={(line) => (window.mdDatabaseBlockOf && window.mdDatabaseBlockOf(line))
            || (window.mdEmbedBlockOf && window.mdEmbedBlockOf(line))}
          blockFormats={window.mdEmbedFormatsOf} formatBlock={window.mdEmbedFormat}
          renderBlock={({ key, line, width, height, active, edit }) => {
            const raw = text.split('\n')[line];
            const databaseId = window.mdDatabaseIdFromLine ? window.mdDatabaseIdFromLine(raw) : '';
            if (databaseId) return <window.DatabaseBlock key={key} databaseId={databaseId}
              width={width} height={height} accent={accent} onRemove={() => edit('')}/>;
            const e = window.mdEmbedLine ? window.mdEmbedLine(raw) : null;
            if (!e) return null;
            return (
              <window.EmbedCard key={key} embed={e} raw={raw} light={light}
                accent={accent} active={active} width={width} height={height}
                title={recordName || ''} onEdit={edit} />);
          }}
          /* No padding-bottom here: `fill` owns the foot of the record, and a
             padding set from outside would be the thing the runway is measured
             against rather than the runway. */
          style={{ '--md-title-ink': inkC(0.96), paddingTop: 8,
          '--md-title-lead': isProject && domain ? (MD_LEAD_SIZE + MD_LEAD_GAP) + 'px' : '0' }} />
          {window.BookReviewSection && <window.BookReviewSection refKey={bookRefKey} currentText={text}/>}
          </div>
        </div>

        {/* ── the shared adaptive schedule surface ── */}
        {whenOpen && window.ScheduleOverlay &&
        <window.ScheduleOverlay accent={accent} light={light} value={whenDraft || sched}
        showAnchors={showAnchors} weekStart={weekStart}
        dateOnly={isProject || backlog} title={isProject ? 'Target' : 'Schedule'}
        onChange={setWhenDraft}
        onConfirm={() => {
          /* A backlog record's confirm SCHEDULES it (OD-3): the chosen day
             goes up the path that already knew how — md-app moves the row out
             of the backlog and reopens this record as a day record, so the
             sheet does not keep a schedule of its own here. Date only, above,
             for the same reason: the time of day is chosen in the reopened
             sheet, and a ribbon whose choice would be discarded is a lie. */
          if (backlog && onBacklogSchedule) {
            const chosen = whenDraft || sched;
            setWhenDraft(null);setWhenOpen(false);
            /* …unless the record has not been created yet (P-5). Then there is
               no row to move out of the backlog, so the day is REMEMBERED and
               Create does both acts in order — written into the backlog, then
               scheduled out of it. Same path, one press later. */
            if (named) { setSched({ ...sched, offset: chosen.offset }); setScheduleOnCreate(true); return; }
            onBacklogSchedule(uidRef.current, window.mdDayKey(chosen.offset || 0));
            return;
          }
          if (whenDraft) setSched(whenDraft);
          setWhenDraft(null);setWhenOpen(false);
        }}
        onDiscard={() => {setWhenDraft(null);setWhenOpen(false);}} />}
  </MdSheetFrame>;

}

// mdDomainColor / mdDomainIcon used to be published from here. They belong to
// md-domain.ts now and it publishes them itself, well before this file loads.
/* ── WHICH SHEET THIS IS — A QUESTION THAT NO LONGER HAS TWO ANSWERS (P4) ─────
   A template rule stood here (CS-7). It decided who got the properties band, on
   "parenthood, not novelty": a record made inside a project has four facts
   it did not choose and cannot see, and a band of rows all reading "—" over a
   thought captured from the day's "+" would have been worse than nothing.

   That was the right rule for a band that could only be justified some of the
   time. It stops being a question at all now that the properties are the pills
   the beginning wears: three answers, each of which a record ALWAYS has — where
   it stands, when it runs, what it is about — so there is one body, drawn for
   everybody, and nothing left to decide. The `template` prop retires with it;
   callers that passed one are simply not read.

   (The band itself, `MdPropsBand`, goes the same way. Its rows and the pills
   were two views of one fact, which is what a properties panel IS — but two
   COMPONENTS of one fact is drift waiting to happen, and the panel is still
   there, holding the version with the words attached.) */

/* The measure is published because it is a fact about the RECORD rather than
   about this file: the band rides it (CS-7), and CS-11's panel docks only where
   the frame can carry `MD_MEASURE + panel`. A second 780 typed elsewhere is a
   measure that stops matching the writing the day either one moves. */
/* The frame and the stand are published for md-projects.jsx, which loads after
   this file and begins a project in the same sheet a record is begun in
   (CS-8). Two exports rather than one because a tenant needs both halves: the
   stand is a hook and has to be called from the tenant's own render, so the
   tenant can hand its own escape and its own write law to a frame that knows
   about neither. */
Object.assign(window, { ComposeSheet, mdPrimeKeyboard, MD_MEASURE,
  MdSheetFrame, useMdSheetStand, MdSheetDone, MD_SHEET_INSET,
  MdTagInspector: TagInspector });
